import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './AuthContext';
import { firebaseConfigError } from './firebase';
import Header from './Header';
import HomePage from './HomePage';
import { AlertsPage, HowItWorks } from './InfoPages';
import LocationPage from './LocationPage';
import Login from './Login';
import { fetchSensorLocations } from './sensorData';
import { loadUnitSystem, saveUnitSystem } from './units';

const REQUIRE_LOGIN = process.env.REACT_APP_REQUIRE_LOGIN === 'true';

const SensorRedirect = () => {
  const { deviceId } = useParams();
  return <Navigate to={`/location/${encodeURIComponent(deviceId)}`} replace />;
};

const FloodCenter = ({ user, onLogout }) => {
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unitSystem, setUnitSystem] = useState(loadUnitSystem);

  useEffect(() => {
    let cancelled = false;
    fetchSensorLocations()
      .then((list) => {
        if (!cancelled) setSensors(list);
      })
      .catch((err) => {
        console.error('[App] sensor_locations:', err);
        if (!cancelled) setError('Could not load sensors.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const changeUnitSystem = useCallback((system) => {
    setUnitSystem(system);
    saveUnitSystem(system);
  }, []);

  return (
    <div className="app">
      <Header user={user} onLogout={onLogout} />
      <Routes>
        <Route
          path="/"
          element={(
            <HomePage
              sensors={sensors}
              loading={loading}
              error={error}
              unitSystem={unitSystem}
            />
          )}
        />
        <Route
          path="/location/:deviceId"
          element={(
            <LocationPage
              sensors={sensors}
              unitSystem={unitSystem}
              onUnitSystemChange={changeUnitSystem}
            />
          )}
        />
        <Route path="/sensors/:deviceId" element={<SensorRedirect />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/alerts" element={<AlertsPage sensors={sensors} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

const AuthGate = () => {
  const { user, logout } = useAuth();
  return user ? <FloodCenter user={user} onLogout={logout} /> : <Login />;
};

const App = () => (
  <BrowserRouter>
    {firebaseConfigError ? (
      <div className="app">
        <p className="banner-error" role="alert">{firebaseConfigError}</p>
      </div>
    ) : REQUIRE_LOGIN ? (
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    ) : (
      <FloodCenter />
    )}
  </BrowserRouter>
);

export default App;
